import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, IconButton, Select, MenuItem, FormControl, InputLabel, Button, Snackbar, Alert, Portal, TextField, Tooltip } from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Modal from '../modal';
import { TimeSlotName } from '../../../services/treatmentAdministration';
import { 
    formatDateToISO, 
    getWeekStart, 
    getWeeksInMonth, 
    navigateWeek,
    getMondayBasedDayOfWeek 
} from '../../../utils/dateUtils';
import { useSnackBarState, useResearcher } from '../../../hooks';
import { researcherFullName, dateAndTimeFromPostgresString } from '../../../utils';

interface TreatmentInpatientChartProps extends LocalizeContextProps {
    data: Array<{
        drug: string;
        quantity: number;
        frequency: string;
        route: string;
        stopped: string;
        treatmentPostId?: number;
        administrations: { [dateKey: string]: {
            timeSlots: { [slotName: string]: {
                status: "successful" | "not successful" | null;
                administrationId?: number;
                administeredBy?: string;
                createdAt?: string;     
            }};
        }};
    }>;
    patientUuid?: string;
    uuidInvestigation?: string;
    language?: string;
    onStatusUpdate?: (drugIndex: number, dateKey: string, timeSlotName: TimeSlotName, status: "successful" | "not successful" | null, reasonNotAdministered?: string) => void;
}

const TreatmentInpatientChart: React.FC<TreatmentInpatientChartProps> = ({ 
    data: initialData, 
    patientUuid, 
    uuidInvestigation,
    language,
    translate,
    onStatusUpdate 
}) => {
    const [data, setData] = useState(initialData);
    const daysInChart = 7;
    const timeSlotLabels = ['Morning', 'Noon', 'Evening', 'Bedtime'];
    const timeSlotNames: TimeSlotName[] = ['morning', 'noon', 'evening', 'bedtime'];

    // Add state for week navigation
    const [currentDate, setCurrentDate] = useState(new Date());
    const [weekStartDate, setWeekStartDate] = useState<Date>(new Date());

    // Add state for week selector modal
    const [weekSelectorOpen, setWeekSelectorOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

    // Update local data when initialData changes
    useEffect(() => {
        setData(initialData);
    }, [initialData]);

    // Initialize week start date on mount (Monday-based week)
    useEffect(() => {
        const today = new Date();
        const startOfWeek = getWeekStart(today, 'monday');
        
        setWeekStartDate(startOfWeek);
        setCurrentDate(today);
    }, []);

    // getWeeksInMonth is now imported from dateUtils

    // Format week range for display
    const formatWeekRange = (weekStart: Date): string => {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        const startDay = weekStart.getDate();
        const endDay = weekEnd.getDate();
        const startMonth = weekStart.toLocaleString('default', { month: 'short' });
        const endMonth = weekEnd.toLocaleString('default', { month: 'short' });
        
        if (startMonth === endMonth) {
            return `${startDay}-${endDay} ${startMonth}`;
        } else {
            return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
        }
    };

    // Open week selector modal
    const openWeekSelector = () => {
        // Initialize with current week's year and month
        setSelectedYear(weekStartDate.getFullYear());
        setSelectedMonth(weekStartDate.getMonth());
        
        // Find which week index in that month
        const weeksInMonth = getWeeksInMonth(weekStartDate.getFullYear(), weekStartDate.getMonth());
        const currentWeekIndex = weeksInMonth.findIndex(
            week => week.getTime() === weekStartDate.getTime()
        );
        setSelectedWeekIndex(currentWeekIndex >= 0 ? currentWeekIndex : 0);
        
        setWeekSelectorOpen(true);
    };

    // Apply selected week
    const applyWeekSelection = () => {
        const weeksInMonth = getWeeksInMonth(selectedYear, selectedMonth);
        if (weeksInMonth[selectedWeekIndex]) {
            setWeekStartDate(weeksInMonth[selectedWeekIndex]);
        }
        setWeekSelectorOpen(false);
    };

    // Calculate days of the current week
    const getWeekDays = () => {
        const days = [];
        for (let i = 0; i < daysInChart; i++) {
            const day = new Date(weekStartDate);
            day.setDate(weekStartDate.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const weekDays = getWeekDays();
    const currentMonth = weekStartDate.toLocaleString('default', { month: 'long', year: 'numeric' });

    // Navigation functions - Unlimited navigation (no restrictions)
    const goToPreviousWeek = () => {
        setWeekStartDate(navigateWeek(weekStartDate, 'prev'));
    };

    const goToNextWeek = () => {
        setWeekStartDate(navigateWeek(weekStartDate, 'next'));
    };

    const [popupOpen, setPopupOpen] = useState(false);                      //For the popup that appears after clicking "Provide reason for not administering the drug" 
    const [selectedCell, setSelectedCell] = useState<{
        drugIndex: number;
        day: string;
        timeSlot: TimeSlotName;
    } | null>(null);
    const [selectedReason, setSelectedReason] = useState<string>('');

    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    const notAdministeringReasons = [
        { code: 'A', reasonNotAdministered: 'Absent' },
        { code: 'F', reasonNotAdministered: 'Fasting' },
        { code: 'L', reasonNotAdministered: 'On leave' },
        { code: 'N', reasonNotAdministered: 'Not available - obtain supply or contact prescriber' },
        { code: 'R', reasonNotAdministered: 'Refused - notify prescriber' },
        { code: 'S', reasonNotAdministered: 'Self administered' },
        { code: 'V', reasonNotAdministered: 'Vomiting' },
        { code: 'W', reasonNotAdministered: 'Withheld - enter reason in clinical record' }
    ];

    // Add new state for context menu
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
    const [contextMenuCell, setContextMenuCell] = useState<{
        drugIndex: number;
        day: string; // Changed from number to string
        timeSlot: TimeSlotName;
    } | null>(null);

    // Add a new state for the cell action menu (for non-empty cells), i.e. the menu that says "Mark as ✓" or "Mark as ✗"
    const [cellActionMenuOpen, setCellActionMenuOpen] = useState(false);
    const [cellActionMenuPosition, setCellActionMenuPosition] = useState<{ x: number; y: number } | null>(null);
    const [cellActionMenuCell, setCellActionMenuCell] = useState<{
        drugIndex: number;
        day: string; // Changed from number to string
        timeSlot: TimeSlotName;
        currentStatus: "successful" | "not successful";
    } | null>(null);


    // Validation function to check if date is editable (today or yesterday only)
    const isDateEditable = (dateKey: string): boolean => {
        const cellDate = new Date(dateKey);
        cellDate.setHours(0, 0, 0, 0); // Start of the cell's day
        
        const now = new Date();
        const today = new Date(now);
        today.setHours(0, 0, 0, 0); // Start of today
        
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1); // Start of yesterday
        
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1); // Start of tomorrow
        
        // Allow only today and yesterday
        return cellDate >= yesterday && cellDate < tomorrow;
    };

    const showValidationError = () => {
        setShowSnackbar({
            show: true,
            message: <Translate id="hospital.treatment-inpatient-showValidationError" />,
            severity: 'warning'
        });
    };

    const AdministrationTooltip: React.FC<{
        administeredBy?: string;
        createdAt?: string;
        language?: string;
        translate: (id: string) => any;
        children: React.ReactElement;
    }> = ({ administeredBy, createdAt, language = 'en', translate, children }) => {
        const { researcher, loading } = useResearcher(administeredBy || '');
        
        const tooltipLines: React.ReactNode[] = [];
        
        if (administeredBy) {
            if (loading) {
                tooltipLines.push(
                    <div key="loading">
                        {translate('hospital.treatment-inpatient-loading').toString()}
                    </div>
                );
            } else if (researcher) {
                tooltipLines.push(
                    <div key="administered-by">
                        {translate('hospital.treatment-inpatient-administered-by').toString()}: {researcherFullName(researcher)}
                    </div>
                );
            } else {
                tooltipLines.push(
                    <div key="not-found">
                        {translate('hospital.treatment-inpatient-administered-by').toString()}: {translate('hospital.treatment-inpatient-researcher-not-found')}
                    </div>
                );
            }
        }
        
        if (createdAt) {
            const formattedDate = dateAndTimeFromPostgresString(language, createdAt);
            tooltipLines.push(
                <div key="date">
                    {translate('hospital.treatment-inpatient-date').toString()}: {formattedDate}
                </div>
            );
        }
        
        if (tooltipLines.length === 0) {
            return children;
        }
        
        return (
            <Tooltip 
                title={
                    <Box>
                        {tooltipLines}
                    </Box>
                }
                arrow
                placement="top"
            >
                {children}
            </Tooltip>
        );
    };

  
    // Helper to render the status cell
    const renderStatusCell = (status: "successful" | "not successful" | null, drugIndex: number, dateKey: string, timeSlotName: TimeSlotName, administeredBy?: string, createdAt?: string) => {
        let content;
        let backgroundColor = 'white';
        let color = 'black';
      
        switch (status) {
            case 'successful':
                content = <CheckIcon sx={{ fontSize: 16, color: 'green' }} />;
                break;
            case 'not successful':
                content = <CloseIcon sx={{ fontSize: 16, color: 'red' }} />;
                break;
            default:
                content = null;
                backgroundColor = '#e0e0e0';
                break;
        }
      
        //return (
        const cellContent = (
            <Box
                sx={{
                    width: '100%',
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ccc',
                    backgroundColor: backgroundColor,
                    color: color,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: '#f0f0f0'
                    }
                }}
                onContextMenu={(e) => {
                    e.preventDefault();

                    if (!isDateEditable(dateKey)) {
                        showValidationError();
                        return;
                    }

                    setContextMenuCell({ drugIndex, day: dateKey, timeSlot: timeSlotName });  // Store dateKey in day field
                    setContextMenuPosition({ x: e.clientX, y: e.clientY });
                    setContextMenuOpen(true);
                }}
                onClick={(e) => {
                    if (!isDateEditable(dateKey)) {
                        showValidationError();
                        return;
                    }

                    if (status === 'not successful' || status === 'successful') {
                        // For non-empty cells, show action menu
                        setCellActionMenuCell({ drugIndex, day: dateKey, timeSlot: timeSlotName, currentStatus: status });
                        const rect = e.currentTarget.getBoundingClientRect();
                        setCellActionMenuPosition({ x: rect.right + 5, y: rect.top });
                        setCellActionMenuOpen(true);
                    } else {
                        // For empty cells, show context menu
                        setContextMenuCell({ drugIndex, day: dateKey, timeSlot: timeSlotName });

                        const rect = e.currentTarget.getBoundingClientRect();
                        setContextMenuPosition({ x: rect.right + 5, y: rect.top });
                        setContextMenuOpen(true);
                    }
                }}
            >
                {content}
            </Box>
        );

        // Wrap with tooltip only if there's data to show
        if ((status === 'successful' || status === 'not successful') && (administeredBy || createdAt)) {
            return (
                <AdministrationTooltip 
                    administeredBy={administeredBy}
                    createdAt={createdAt}
                    language={language}
                    translate={translate} 
                >
                    {cellContent}
                </AdministrationTooltip>
            );

        }

        return cellContent;

    };

    const updateStatusInData = (drugIndex: number, dateKey: string, timeSlotName: TimeSlotName, status: "successful" | "not successful" | null, reasonNotAdministered?: string) => {
        if (onStatusUpdate) {
            onStatusUpdate(drugIndex, dateKey, timeSlotName, status, reasonNotAdministered);
        }
    };

    const handleContextMenuAction = (action: 'successful' | 'not successful') => {
        if (contextMenuCell) {

            if (action === 'not successful') {
                // For "not successful", open the reason popup instead of directly updating
                setSelectedCell({
                    drugIndex: contextMenuCell.drugIndex,
                    day: contextMenuCell.day,
                    timeSlot: contextMenuCell.timeSlot
                });
                setPopupOpen(true);
            } 
            
            else {
                // For "successful", directly update as before
                // Need to convert day number to dateKey here
                updateStatusInData(contextMenuCell.drugIndex, contextMenuCell.day, contextMenuCell.timeSlot, action);
            }
            
        }
        setContextMenuOpen(false);
        setContextMenuCell(null);
        setContextMenuPosition(null);
    };
  
    return (
        <Paper sx={{ width: '100%', overflowX: 'auto', boxSizing: 'border-box', position: 'relative' }}>

            {/* Month Header with Navigation */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                p: 2, 
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #ccc',
                gap: 1
            }}>
                <IconButton 
                    onClick={goToPreviousWeek}
                    size="small"
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
                        {currentMonth}
                    </Typography>
                    <IconButton 
                        onClick={openWeekSelector}
                        size="small"
                        color="primary"
                        sx={{ 
                            '&:hover': { backgroundColor: 'rgba(63, 81, 181, 0.1)' }
                        }}
                    >
                        <CalendarMonthIcon />
                    </IconButton>
                </Box>
                
                <IconButton 
                    onClick={goToNextWeek}
                    size="small"
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>

            {/* Week Selector Modal */}
            {weekSelectorOpen && (
                <Modal
                    open={weekSelectorOpen}
                    title="Select Week"
                    closeModal={() => setWeekSelectorOpen(false)}                 /*TODO: In the closeModal I want to close all visual elements */
                    maxWidth="sm"
                >
                    <Box sx={{ p: 2, minWidth: 400 }}>
                        <Grid container spacing={2}>
                            {/* Year Selector */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Year</InputLabel>
                                    <Select
                                        value={selectedYear}
                                        label="Year"
                                        onChange={(e) => {
                                            setSelectedYear(Number(e.target.value));
                                            setSelectedWeekIndex(0);
                                        }}
                                    >
                                        {Array.from({ length: 201 }, (_, i) => {
                                            const year = 1900 + i;
                                            return (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Month Selector */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Month</InputLabel>
                                    <Select
                                        value={selectedMonth}
                                        label="Month"
                                        onChange={(e) => {
                                            setSelectedMonth(Number(e.target.value));
                                            setSelectedWeekIndex(0);
                                        }}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => {
                                            const monthName = new Date(2000, i, 1).toLocaleString('default', { month: 'long' });
                                            return (
                                                <MenuItem key={i} value={i}>
                                                    {monthName}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Week Selector */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Week</InputLabel>
                                    <Select
                                        value={selectedWeekIndex}
                                        label="Week"
                                        onChange={(e) => setSelectedWeekIndex(Number(e.target.value))}
                                    >
                                        {getWeeksInMonth(selectedYear, selectedMonth).map((weekStart, index) => (
                                            <MenuItem key={index} value={index}>
                                                Week {index + 1}: {formatWeekRange(weekStart)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Apply Button */}
                            <Grid item xs={12}>
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    onClick={applyWeekSelection}
                                    sx={{ mt: 2 }}
                                >
                                    Apply
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Modal>
            )}
            {data.map((drugData, drugIndex) => {
                const isStopped = drugData.stopped === "1";
                
                return (
                    <Grid container key={drugIndex} wrap="nowrap" sx={{ mb: 2, position: 'relative', width: '100%' }}>
                        {/* Drug Information Column - existing code */}
                        <Grid item>
                            <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, backgroundColor: '#f0f0f0' }}>
                                <Typography variant="subtitle2">Drug</Typography>
                            </Box>
                            <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, backgroundColor: '#3f51b5', color: 'white', mt: -1 }}>
                                <Typography variant="body2">{drugData.drug}</Typography>
                            </Box>
                            <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, mt: -1 }}>
                                <Typography variant="caption">Q:{drugData.quantity}</Typography>
                            </Box>
                            <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, mt: -1 }}>
                                <Typography variant="caption">F:{drugData.frequency}</Typography>
                            </Box>
                            <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, mt: -1 }}>
                                <Typography variant="caption">R:{drugData.route}</Typography>
                            </Box>
                        </Grid>

                        {/* Time Slots and Daily Grid */}
                        <Grid item sx={{ flex: 1, minWidth: 0 }}>
                            <Grid container direction="column" wrap="nowrap" sx={{ width: '100%' }}>
                                {/* Days Header - Updated to show actual dates */}
                                <Grid item>
                                    <Grid container wrap="nowrap" sx={{ width: '100%' }}>
                                        <Box sx={{ width: 80, minWidth: 80, border: '1px solid #ccc', p: 1, backgroundColor: '#f0f0f0' }}>
                                            <Typography variant="subtitle2">Time</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flex: 1 }}>
                                            {weekDays.map((day, i) => {
                                                const isToday = day.toDateString() === new Date().toDateString();
                                                return (
                                                    <Box
                                                        key={`day-header-${i}`}
                                                        sx={{
                                                            flex: '1 1 0',
                                                            minWidth: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            border: '1px solid #ccc',
                                                            borderLeft: i === 0 ? '1px solid #ccc' : 'none',
                                                            backgroundColor: isToday ? '#4caf50' : '#e0e0e0',
                                                            color: isToday ? 'white' : 'inherit',
                                                            fontWeight: isToday ? 'bold' : 'normal',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <Typography variant="caption">{day.getDate()}</Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Administration Rows - existing code */}
                                {timeSlotLabels.map((label, timeSlotIndex) => (
                                    <Grid item key={`time-slot-row-${label}`}>
                                        <Grid container wrap="nowrap" sx={{ width: '100%' }}>
                                            <Box sx={{ width: 80, minWidth: 80, border: '1px solid #ccc', borderTop: 'none', p: 1 }}>
                                                <Typography variant="caption">{label}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flex: 1 }}>
                                                {weekDays.map((day, dayIndex) => {
                                                    const dateKey = formatDateToISO(day);
                                                    const administrationForDay = drugData.administrations?.[dateKey];
                                                    const timeSlotName: TimeSlotName = timeSlotNames[timeSlotIndex];
                                                    const status = administrationForDay?.timeSlots?.[timeSlotName]?.status || null;
                                                    const administeredBy = administrationForDay?.timeSlots?.[timeSlotName]?.administeredBy;
                                                    const createdAt = administrationForDay?.timeSlots?.[timeSlotName]?.createdAt;
                                                    
                                                    return (
                                                        <Box key={`cell-${dateKey}-${timeSlotIndex}`} sx={{ flex: '1 1 0', minWidth: 24 }}>
                                                            {renderStatusCell(status, drugIndex, dateKey, timeSlotName, administeredBy, createdAt)}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Red Opaque Overlay for Stopped Treatments - existing code */}
                        {isStopped && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(244, 67, 54, 0.3)',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                    borderRadius: '4px'
                                }}
                            />
                        )}
                    </Grid>
                );
            })}

            {/*------Debug*/}

            {/* Popup Modal for Reason Selection */}
            {popupOpen && (
                <Modal 
                    open={popupOpen}
                    title="Reason for not administering"
                    closeModal={() => setPopupOpen(false)}          /*TODO: TODO: In the closeModal I want to close all visual elements */
                    maxWidth="sm"
                >
                <Box sx={{ p: 2 }}>
                    {notAdministeringReasons.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        }
                        }}

                        /*------Debug*/
                        onClick={() => {
                            setSelectedReason(item.code);

                            if (selectedCell) {
                                updateStatusInData(
                                    selectedCell.drugIndex, 
                                    selectedCell.day,
                                    selectedCell.timeSlot, 
                                    'not successful',  // Set status to not successful
                                    item.reasonNotAdministered
                                );
                            }

                            setPopupOpen(false);
                            setSelectedCell(null);  // Clear selected cell
                            setSelectedReason('');
                        }}
                        /*------Debug*/
                    >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.reasonNotAdministered}
                        </Typography>
                        <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: selectedReason === item.code ? '2px solid #1976d2' : '2px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: selectedReason === item.code ? '#1976d2' : 'transparent',
                            color: selectedReason === item.code ? 'white' : '#333',
                            fontWeight: 'bold'
                        }}
                        >
                        {item.code}
                        </Box>
                    </Box>
                    ))}
                </Box>
                </Modal>
            )}
            {/*------Debug*/}

            {(contextMenuOpen || cellActionMenuOpen) && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1299, // Just below the menu (which has zIndex 1300)
                            backgroundColor: 'transparent'
                        }}
                        onClick={() => {
                            if (contextMenuOpen) {
                                setContextMenuOpen(false);
                                setContextMenuCell(null);
                                setContextMenuPosition(null);
                            }
                            if (cellActionMenuOpen) {
                                setCellActionMenuOpen(false);
                                setCellActionMenuCell(null);
                                setCellActionMenuPosition(null);
                            }
                        }}
                    />
                </Portal>    
            )}

            {/* Context Menu for Empty Cells */}
            {contextMenuOpen && contextMenuPosition && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: contextMenuPosition.y,
                            left: contextMenuPosition.x,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1300,
                            minWidth: 120
                        }}
                        onMouseLeave={() => {
                            setContextMenuOpen(false);
                            setContextMenuCell(null);
                            setContextMenuPosition(null);
                        }}
                    >
                        <Box
                            sx={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                            onClick={() => handleContextMenuAction('successful')}
                        >
                            <CheckIcon sx={{ fontSize: 16, color: 'green' }} />
                            <Typography variant="body2">Mark as ✓</Typography>
                        </Box>
                        <Box
                            sx={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                            onClick={() => handleContextMenuAction('not successful')}
                        >
                            <CloseIcon sx={{ fontSize: 16, color: 'red' }} />
                            <Typography variant="body2">Mark as ✗</Typography>
                        </Box>
                    </Box>

                </Portal>
            )}

            {/* Cell Action Menu for Non-Empty Cells. It only appears when menu [should be visible] & [we know where to position it] & [we know which cell it's for]*/}
            {cellActionMenuOpen && cellActionMenuPosition && cellActionMenuCell && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: cellActionMenuPosition.y,
                            left: cellActionMenuPosition.x,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1300,
                            minWidth: 200
                        }}
                        onMouseLeave={() => {
                            setCellActionMenuOpen(false);
                            setCellActionMenuCell(null);
                            setCellActionMenuPosition(null);
                        }}
                    >
                        <Box
                            sx={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                            onClick={() => {
                                if (cellActionMenuCell) {
                                    updateStatusInData(
                                        cellActionMenuCell.drugIndex,
                                        cellActionMenuCell.day,
                                        cellActionMenuCell.timeSlot,
                                        null
                                    );
                                }
                                setCellActionMenuOpen(false);
                                setCellActionMenuCell(null);
                                setCellActionMenuPosition(null);
                            }}
                        >
                            <Typography variant="body2">Remove content from the cell</Typography>
                        </Box>
                        {/* {cellActionMenuCell.currentStatus === 'not successful' && (
                            <Box
                                sx={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                                //------Debug
                                onClick={() => {
                                    if (cellActionMenuCell) {
                                        setSelectedCell({
                                            drugIndex: cellActionMenuCell.drugIndex,
                                            day: cellActionMenuCell.day,
                                            timeSlot: cellActionMenuCell.timeSlot
                                        });
                                        setPopupOpen(true);
                                    }
                                    setCellActionMenuOpen(false);
                                    setCellActionMenuCell(null);
                                    setCellActionMenuPosition(null);
                                }}
                                //------Debug
                            >
                                <Typography variant="body2">Provide reason for not administering the drug</Typography>
                            </Box>
                        )} */}
                    </Box>
                </Portal>
            )}

            {/* Snackbar for validation errors */}
            <Snackbar 
                open={showSnackbar.show} 
                autoHideDuration={4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={showSnackbar.severity || 'warning'} 
                    sx={{ width: '100%' }}
                >
                    {showSnackbar.message}
                </Alert>
            </Snackbar>

        </Paper>
    );
};

export default withLocalize(TreatmentInpatientChart);